import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import {
  romanianExperiences,
  romaniaExperiencesIntro,
  type RomanianExperience,
} from '@/data/romaniaExperiences';

/* ------------------------------------------------------------------ */
/* Romanian Experiences — immersive homepage section.                  */
/* Sits below Romanian Destinations and above Discover Romania (map).  */
/* Colors use the shared navy/gold tokens which the .theme-romania     */
/* skin automatically re-maps to maroon/gold.                          */
/* ------------------------------------------------------------------ */

/* Lightweight reveal-on-scroll: fades cards up as they enter view. */
const useReveal = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, visible };
};

const ExperienceCard: React.FC<{
  exp: RomanianExperience;
  index: number;
}> = ({ exp, index }) => {
  const navigate = useNavigate();
  const { ref, visible } = useReveal();
  const wide = exp.featured;

  const goToExperience = () => navigate(`/romania/experiences#${exp.slug}`);

  return (
    <div
      ref={ref}
      className={`group relative overflow-hidden cursor-pointer ${
        wide ? 'sm:col-span-2 aspect-[16/10] sm:aspect-[21/10]' : 'aspect-[3/4]'
      } transition-all duration-700 ease-out will-change-transform ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.45)]`}
      style={{ transitionDelay: `${Math.min(index % 4, 3) * 90}ms` }}
      onClick={goToExperience}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToExperience();
        }
      }}
      aria-label={`${exp.title} — learn more`}
    >
      {/* Photo */}
      <img
        src={exp.image}
        alt={exp.alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.07]"
      />

      {/* Readability overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/10 transition-opacity duration-500 group-hover:from-black/85 group-hover:via-black/40" />
      <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />

      {/* Gold corner index */}
      <span className="absolute top-5 right-5 font-serif text-3xl md:text-4xl font-light text-white/25 group-hover:text-[#C9A961]/60 transition-colors duration-500">
        {String(index + 1).padStart(2, '0')}
      </span>

      {/* Content */}
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 flex flex-col justify-end">
        <div className="flex items-center gap-2 mb-2.5 opacity-90">
          <span className="w-6 h-[1px] bg-[#C9A961] transition-all duration-500 group-hover:w-10" />
          <span className="font-sans text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">
            {exp.tagline}
          </span>
        </div>

        <h3
          className={`font-serif text-white font-light leading-snug mb-1 transition-transform duration-500 group-hover:-translate-y-0.5 ${
            wide ? 'text-2xl md:text-4xl' : 'text-xl md:text-2xl'
          }`}
        >
          {exp.title}
        </h3>

        {/* Revealed on hover / keyboard focus */}
        <div className="overflow-hidden max-h-0 opacity-0 transition-all duration-500 ease-out group-hover:max-h-44 group-hover:opacity-100 group-focus-within:max-h-44 group-focus-within:opacity-100">
          <p className="font-sans text-[12px] md:text-[13px] text-white/85 leading-relaxed pt-2 mb-4 max-w-xl">
            {exp.description}
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToExperience();
            }}
            className="inline-flex items-center gap-2 font-sans text-[10px] tracking-[0.25em] uppercase text-white border border-[#C9A961]/70 px-5 py-2.5 hover:bg-[#C9A961] hover:border-[#C9A961] transition-all duration-300"
          >
            Learn More <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Mobile hint (hover reveals don't exist on touch) */}
        <span className="mt-2 inline-flex items-center gap-1.5 font-sans text-[9px] tracking-[0.25em] uppercase text-white/50 md:hidden">
          Tap to explore <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </div>
  );
};

const RomanianExperiencesSection: React.FC = () => {
  const navigate = useNavigate();
  const { ref: headRef, visible: headVisible } = useReveal();

  return (
    <section id="experiences" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      {/* Subtle luxe backdrop flourishes */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#C9A961]/[0.06] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#1A3B5C]/[0.05] blur-3xl" />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef}
          className={`text-center mb-14 lg:mb-20 transition-all duration-700 ease-out ${
            headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-10 h-[1px] bg-[#C9A961]" />
            <span className="inline-flex items-center gap-2 font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              <Sparkles className="w-3.5 h-3.5" /> Beyond Destinations
            </span>
            <span className="w-10 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6 gold-underline">
            Romanian Experiences
          </h2>
          <p className="font-sans text-sm md:text-[15px] text-[#777] max-w-2xl mx-auto leading-relaxed mt-10">
            {romaniaExperiencesIntro}
          </p>
        </div>

        {/* Mosaic grid: two cinematic wide cards, then a 4-across gallery */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {romanianExperiences.map((exp, i) => (
            <ExperienceCard key={exp.slug} exp={exp} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14 lg:mt-16">
          <button
            onClick={() => navigate('/romania/experiences')}
            className="group inline-flex items-center gap-3 font-sans text-[11px] tracking-[0.3em] uppercase px-12 py-4 bg-[#1A3B5C] text-white hover:bg-[#C9A961] transition-all duration-500"
          >
            Explore All Experiences
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
          <p className="font-sans text-[11px] text-[#999] tracking-wide mt-4">
            Ten signature ways to live Romania — each one tailored by our concierge team.
          </p>
        </div>
      </div>
    </section>
  );
};

export default RomanianExperiencesSection;
